import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { heroPrompts } from '../data/homepage';
import { AtnaConversationDialog } from './demandes/AtnaConversationDialog';
import { InscriptionDialog } from './loisirs/InscriptionDialog';
import { detectCategory } from '../hooks/useInscriptionConversation';
const PANEL_LAYOUT_ID = 'atna-hero-panel';
type Flow = 'signalement' | 'inscription';

/** Choisit le flux ATNA le plus probable à partir de la phrase du citoyen. */
function detectFlow(text: string): Flow {
  const lower = text.toLowerCase();
  const wantsRegistration = /inscri|cours|activit|camp|session|natation/.test(lower) || detectCategory(lower) !== null;
  return wantsRegistration ? 'inscription' : 'signalement';
}
export function HeroAtna() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [conversation, setConversation] = useState<{
    flow: Flow;
    text: string;
  } | null>(null);
  const openConversation = (text: string, flow?: Flow) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setConversation({
      flow: flow ?? detectFlow(trimmed),
      text: trimmed
    });
    setValue('');
  };
  return <section aria-labelledby="hero-atna-titre" className="w-full rounded-3xl border border-white/60 bg-white p-5 shadow-2xl shadow-ville-navy/20 sm:p-6" data-id="element-1423" data-mp="oog0l8lc0i6l">
      <h2 id="hero-atna-titre" className="sr-only" data-id="element-1424" data-mp="1ccphpx7lohre">
        Assistant ATNA
      </h2>

      {conversation ?
    // Garde la hauteur pendant que la barre se transforme en dialogue.
    <div className="h-[54px]" aria-hidden="true" data-id="element-1425" data-mp="13fyaqprifii1" /> : <motion.div layoutId={PANEL_LAYOUT_ID} style={{
      borderRadius: 999
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1426" data-mp="18blz52y647mh">
          <form onSubmit={event => {
        event.preventDefault();
        openConversation(value);
      }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-1.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1427" data-mp="3t3fr8oinu4s4">
            <button type="button" onClick={() => openConversation(value)} aria-label="Joindre une photo ou un document" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1428" data-mp="1ovbd68z5ljhz">
              <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-1429" data-mp="11wol69zcpoq4" />
            </button>
            <label htmlFor="hero-atna-input" className="sr-only" data-id="element-1430" data-mp="11z1bbaf9j5e9">
              Demander à ATNA
            </label>
            <input id="hero-atna-input" type="text" value={value} onChange={event => setValue(event.target.value)} placeholder="Demander à ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1431" data-mp="2waprth157g0r" />
            <button type="button" aria-label="Dicter votre question" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1432" data-mp="ruunea6e25is">
              <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-1433" data-mp="83pk1a3zlecw" />
            </button>
            <button type="submit" aria-label="Envoyer la question à ATNA" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1434" data-mp="d43wr5gj9ch9">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1435" data-mp="1i3ablfckibod" />
            </button>
          </form>
        </motion.div>}

      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1436" data-mp="1w436402vd6rh">
        {heroPrompts.map(prompt => <li key={prompt.label} data-id="element-1437" data-mp="1s056zpdjnvyf">
            <button type="button" onClick={() => prompt.label === 'Inscrire mon enfant' ? openConversation(prompt.label, 'inscription') : navigate(prompt.to)} className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1438" data-mp="1s3avb2ecmtt3">
              {prompt.label}
            </button>
          </li>)}
      </ul>

      <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-ville-indigo" data-id="element-1439" data-mp="33f7es7tkn81a">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1440" data-mp="38bo435cibsfy" />
        ATNA connaît les services de Brossard
      </p>

      <AnimatePresence data-id="element-1441" data-mp="2ucy8ju5c60tp">
        {conversation?.flow === 'signalement' && <AtnaConversationDialog key="atna-hero-dialog" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1442" data-mp="2ydi1elivmia1" />}
        {conversation?.flow === 'inscription' && <InscriptionDialog key="atna-hero-inscription" initialText={conversation.text} layoutId={PANEL_LAYOUT_ID} onClose={() => setConversation(null)} data-id="element-1443" data-mp="2aq06wi88963d" />}
      </AnimatePresence>
    </section>;
}